import { useEffect, useMemo } from 'react';
import { musicGenreCatalogQuery } from '../taste/MusicGenreCatalogLifecycle';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/api';
import { buildEdition, compareEditions } from './generated/model';
import { festivalArtists } from './generated/EditionArtwork';
import { secureRemoteImageUrl } from './generated/remoteImageUrl';
import { buildPersonaData } from './generated/personaData';
import {
  getTastePersonaPresentation,
  getLocalizedTastePersonaTypeName,
  getLocalizedTastePersonaQuip,
} from '../taste/generated/persona';
import {
  getLocalizedTasteGenre,
  setRemoteMusicGenreCatalog,
  useMusicGenreCatalogRevision,
} from '../taste/generated/localization';
import { tastePayload, tasteTranslate } from '../taste/tasteModel';
import { editionPayload } from './editorModel';
export default function useEditionData(user, p, locale) {
  const key = ['musicEdition', user.id, user.nickname],
    enabled = !!user?.id;
  const records = useQuery({
    queryKey: [...key, 'records'],
    queryFn: async ({ signal }) =>
      editionPayload(await api.getAuth('/me/reviews?edition=true', { signal })),
    enabled,
    retry: 1,
    staleTime: 0,
    gcTime: 0,
  });
  const taste = useQuery({
    queryKey: [...key, 'taste'],
    queryFn: async ({ signal }) =>
      tastePayload(await api.getAuth('/me/taste-analysis', { signal })),
    enabled: enabled && p.period === 'all',
    retry: 1,
    staleTime: 60000,
    gcTime: 0,
  });
  const genres = useQuery({
    ...musicGenreCatalogQuery,
    enabled,
  });
  useEffect(() => {
    if (genres.data) setRemoteMusicGenreCatalog(genres.data);
  }, [genres.data]);
  const revision = useMusicGenreCatalogRevision();
  // A refreshed snapshot/period gets its own Seoul calendar cutoff, as in build58.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const asOf = useMemo(() => new Date(), [records.dataUpdatedAt, p.period]);
  const edition = useMemo(
    () => buildEdition(records.data ?? [], p.period, asOf),
    [records.data, p.period, asOf],
  );
  const friend = useQuery({
    queryKey: [...key, 'friend', p.friendNickname],
    queryFn: async ({ signal }) =>
      editionPayload(
        await api.getAuth(`/users/${encodeURIComponent(p.friendNickname)}/reviews`, { signal }),
      ),
    enabled: enabled && !!p.friendNickname && p.friendNickname !== user.nickname,
    retry: 1,
    staleTime: 0,
    gcTime: 0,
  });
  const comparison = useMemo(
    () =>
      friend.data && !friend.isError
        ? {
            ...compareEditions(edition, buildEdition(friend.data, p.period, asOf)),
            nickname: p.friendNickname,
          }
        : undefined,
    [friend.data, friend.isError, p.friendNickname, edition, p.period, asOf],
  );
  const lineup = festivalArtists({ edition, festivalLayout: 'names' }).slice(0, 1);
  const artistQuery = useQuery({
    queryKey: [...key, 'artist', lineup.map((a) => a.key).join('|')],
    enabled: enabled && !!lineup.length,
    retry: false,
    staleTime: 300000,
    gcTime: 0,
    queryFn: async ({ signal }) =>
      Object.fromEntries(
        await Promise.all(
          lineup.map(async (a) => {
            const artist = edition.records.find(
              (r) =>
                r.artist &&
                `${r.artist.artistSource}:${r.artist.sourceId || r.artist.name}` === a.key,
            )?.artist;
            if (!artist) return [a.key, ''];
            try {
              const url = artist.slug
                ? `/artists/by-slug/${encodeURIComponent(artist.slug)}`
                : artist.sourceId
                  ? `/artists?${new URLSearchParams({ id: artist.sourceId, type: artist.artistSource?.toLowerCase() === 'spotify' ? 'spotify' : 'apple_music' })}`
                  : null;
              const response = url ? await api.getPublic(url, { signal }) : null;
              const match = (response?.data?.data ?? response?.data)?.artist;
              return [
                a.key,
                match &&
                String(match.sourceId) === String(artist.sourceId) &&
                match.artistSource === artist.artistSource
                  ? (secureRemoteImageUrl(match.imageUrl) ?? '')
                  : '',
              ];
            } catch (error) {
              if (signal.aborted) throw error;
              return [a.key, ''];
            }
          }),
        ),
      ),
  });
  const t = tasteTranslate(locale),
    persona =
      p.period === 'all' && taste.data?.isReady && !taste.isError
        ? getTastePersonaPresentation(taste.data)
        : null;
  return {
    records,
    taste,
    friend,
    edition,
    asOf,
    comparison,
    artistQuery,
    revision,
    persona: persona
      ? {
          title: getLocalizedTastePersonaTypeName(persona, t),
          quip: getLocalizedTastePersonaQuip(persona, locale, t),
          data: buildPersonaData(taste.data, edition, locale === 'en', (g) =>
            getLocalizedTasteGenre(g, locale),
          ),
        }
      : undefined,
  };
}
